import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';

declare global {
  interface Window {
    FB?: any;
    fbAsyncInit?: () => void;
  }
}

@Injectable({ providedIn: 'root' })
export class WhatsappMetaSdkService {
  private sdkPromise: Promise<void> | null = null;

  private get appId(): string {
    return environment.whatsapp?.appId || '';
  }

  private get configId(): string {
    return environment.whatsapp?.configId || '';
  }

  private get apiVersion(): string {
    return environment.whatsapp?.apiVersion || 'v26.0';
  }

  isConfigured(): boolean {
    return !!this.appId && !!this.configId;
  }

  loadSdk(): Promise<void> {
    if (this.sdkPromise) return this.sdkPromise;

    if (!this.appId) {
      this.sdkPromise = Promise.reject(
        new Error('Meta App ID no configurado (environment.whatsapp.appId)')
      );
      return this.sdkPromise;
    }

    this.sdkPromise = new Promise<void>((resolve, reject) => {
      if (window.FB) {
        resolve();
        return;
      }

      window.fbAsyncInit = () => {
        try {
          window.FB.init({
            appId: this.appId,
            cookie: true,
            xfbml: false,
            version: this.apiVersion,
          });
          resolve();
        } catch (err) {
          reject(err);
        }
      };

      const existing = document.getElementById('facebook-jssdk') as HTMLScriptElement | null;
      if (existing) {
        existing.addEventListener('load', () => window.fbAsyncInit && window.fbAsyncInit());
        existing.addEventListener('error', () => reject(new Error('No se pudo cargar Meta SDK')));
        return;
      }

      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.onerror = () => reject(new Error('No se pudo cargar Meta SDK'));
      document.body.appendChild(script);
    });

    return this.sdkPromise;
  }

  runEmbeddedSignup(): Promise<string> {
    if (!this.configId) {
      return Promise.reject(
        new Error('Configuration ID no configurado (environment.whatsapp.configId)')
      );
    }

    return this.loadSdk().then(
      () =>
        new Promise<string>((resolve, reject) => {
          window.FB.login(
            (response: any) => {
              const code = response?.authResponse?.code;
              if (code) {
                resolve(code);
              } else {
                reject(new Error('Embedded Signup cancelado o sin code'));
              }
            },
            {
              config_id: this.configId,
              response_type: 'code',
              override_default_response_type: true,
              extras: { version: 'v3', feature: 'whatsapp_embedded_signup' },
            }
          );
        })
    );
  }
}
